import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, CheckCircle2, ChevronRight, Mail, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { extractApiErrorMessage } from '@/lib/api-error';
import {
  addEnterpriseEmployee,
  getEnterpriseSettings,
  getEnterpriseTenant,
  updateEnterpriseSettings,
  updateEnterpriseTenant,
} from '@/services/api.client';

/**
 * Tenant admin first-login onboarding wizard.
 *
 * Walks a freshly-activated tenant admin through the four most useful setup
 * tasks before they hit the dashboard cold:
 *   1. Confirm company name + reception hours
 *   2. Configure visitor notification routing
 *   3. Invite first employees (skippable, defaults to one row)
 *   4. Done — pointer back to Account page for revisits
 *
 * Persistence: a localStorage flag keyed by `${userId}:${tenantId}` records
 * dismissal/completion so the wizard never re-opens after the admin has seen
 * it once. Server-side first-login state is overkill for a UX nudge.
 *
 * ADR 009 slice 3 (tier 1) — paying enterprise customers should not land on
 * an empty dashboard wondering "what now".
 */

const STORAGE_PREFIX = 'sg.tenant-onboarding-done';

function storageKey(userId: string, tenantId: string): string {
  return `${STORAGE_PREFIX}:${userId}:${tenantId}`;
}

// eslint-disable-next-line react-refresh/only-export-components
export function isTenantOnboardingDone(userId: string, tenantId: string): boolean {
  try {
    return localStorage.getItem(storageKey(userId, tenantId)) === '1';
  } catch {
    // localStorage unavailable (private browsing, no DOM, etc.) — treat as
    // already-done so we don't trap the admin in an unclosable wizard.
    return true;
  }
}

function markOnboardingDone(userId: string, tenantId: string): void {
  try {
    localStorage.setItem(storageKey(userId, tenantId), '1');
  } catch {
    /* swallow */
  }
}

type Step = 'company' | 'notifications' | 'employees' | 'done';

interface EmployeeRow {
  fullName: string;
  email: string;
}

const EMPTY_EMPLOYEE: EmployeeRow = { fullName: '', email: '' };

interface TenantOnboardingWizardProps {
  userId: string;
  propertyId: string;
  tenantId: string;
  open: boolean;
  onClose: () => void;
}

export function TenantOnboardingWizard({
  userId,
  propertyId,
  tenantId,
  open,
  onClose,
}: TenantOnboardingWizardProps) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('company');

  const tenantQuery = useQuery({
    queryKey: ['enterprise-tenant', propertyId, tenantId],
    queryFn: () => getEnterpriseTenant(propertyId, tenantId),
    enabled: open,
  });

  const settingsQuery = useQuery({
    queryKey: ['enterprise-settings', propertyId, tenantId],
    queryFn: () => getEnterpriseSettings(propertyId, tenantId),
    enabled: open,
  });

  const [companyName, setCompanyName] = useState('');
  const [receptionHours, setReceptionHours] = useState('');
  const [notifyArrival, setNotifyArrival] = useState(true);
  const [notifyWalkin, setNotifyWalkin] = useState(true);
  const [requireApproval, setRequireApproval] = useState(false);
  const [employees, setEmployees] = useState<EmployeeRow[]>([{ ...EMPTY_EMPLOYEE }]);

  // Pre-fill from server when queries resolve.
  useEffect(() => {
    if (tenantQuery.data) {
      setCompanyName(tenantQuery.data.companyName ?? '');
      setReceptionHours(tenantQuery.data.receptionHours ?? '');
    }
  }, [tenantQuery.data]);

  useEffect(() => {
    if (settingsQuery.data) {
      setNotifyArrival(settingsQuery.data.notifyAdminOnArrival);
      setNotifyWalkin(settingsQuery.data.notifyAdminOnWalkin);
      setRequireApproval(settingsQuery.data.requireAdminApprovalForEmployeeInvites);
    }
  }, [settingsQuery.data]);

  const tenantMutation = useMutation({
    mutationFn: (body: { companyName?: string; receptionHours?: string }) =>
      updateEnterpriseTenant(propertyId, tenantId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['enterprise-tenant', propertyId, tenantId] });
    },
    onError: (err) => toast.error(extractApiErrorMessage(err, 'Could not save company details')),
  });

  const settingsMutation = useMutation({
    mutationFn: (body: {
      notifyAdminOnArrival: boolean;
      notifyAdminOnWalkin: boolean;
      requireAdminApprovalForEmployeeInvites: boolean;
    }) => updateEnterpriseSettings(propertyId, tenantId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['enterprise-settings', propertyId, tenantId] });
    },
    onError: (err) =>
      toast.error(extractApiErrorMessage(err, 'Could not save notification preferences')),
  });

  const employeeMutation = useMutation({
    mutationFn: (body: { fullName: string; email: string }) =>
      addEnterpriseEmployee(propertyId, tenantId, body),
  });

  function close() {
    markOnboardingDone(userId, tenantId);
    onClose();
  }

  async function handleNext() {
    if (step === 'company') {
      if (companyName.trim() || receptionHours.trim()) {
        await tenantMutation.mutateAsync({
          ...(companyName.trim() && { companyName: companyName.trim() }),
          ...(receptionHours.trim() && { receptionHours: receptionHours.trim() }),
        });
      }
      setStep('notifications');
      return;
    }
    if (step === 'notifications') {
      await settingsMutation.mutateAsync({
        notifyAdminOnArrival: notifyArrival,
        notifyAdminOnWalkin: notifyWalkin,
        requireAdminApprovalForEmployeeInvites: requireApproval,
      });
      setStep('employees');
      return;
    }
    if (step === 'employees') {
      const valid = employees.filter((e) => e.fullName.trim() && e.email.trim());
      if (valid.length > 0) {
        try {
          for (const emp of valid) {
            await employeeMutation.mutateAsync({
              fullName: emp.fullName.trim(),
              email: emp.email.trim().toLowerCase(),
            });
          }
          void qc.invalidateQueries({
            queryKey: ['enterprise-employees', propertyId, tenantId],
          });
          toast.success(`${valid.length} ${valid.length === 1 ? 'employee' : 'employees'} invited`);
        } catch (err) {
          toast.error(extractApiErrorMessage(err, 'Could not invite all employees'));
        }
      }
      setStep('done');
      return;
    }
    if (step === 'done') {
      close();
    }
  }

  function addEmployeeRow() {
    setEmployees((prev) => [...prev, { ...EMPTY_EMPLOYEE }]);
  }

  function updateEmployee(index: number, patch: Partial<EmployeeRow>) {
    setEmployees((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  }

  const stepLabels: Record<Step, string> = {
    company: 'Step 1 of 3 · Company',
    notifications: 'Step 2 of 3 · Notifications',
    employees: 'Step 3 of 3 · Employees',
    done: 'All set',
  };

  const isPending =
    tenantMutation.isPending || settingsMutation.isPending || employeeMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent aria-describedby={undefined} className="max-w-lg">
        <DialogHeader>
          <p className="text-xs text-muted-foreground">{stepLabels[step]}</p>
          <DialogTitle>
            {step === 'company' && (
              <span className="flex items-center gap-2">
                <Building2 className="size-5" />
                Welcome — let’s set up your company
              </span>
            )}
            {step === 'notifications' && 'How should you hear about visitors?'}
            {step === 'employees' && 'Invite your first employees'}
            {step === 'done' && (
              <span className="flex items-center gap-2">
                <CheckCircle2 className="size-5 text-[var(--status-green-text)]" />
                You’re all set
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {step === 'company' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Confirm how your company appears to visitors and the building manager.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="onb-company">Company name</Label>
              <Input
                id="onb-company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Corporation"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="onb-reception">Reception hours</Label>
              <Input
                id="onb-reception"
                value={receptionHours}
                onChange={(e) => setReceptionHours(e.target.value)}
                placeholder="08:00–18:00 weekdays"
              />
              <p className="text-xs text-muted-foreground">
                Shown to visitors on their invitation. Leave blank for now if you’re not sure.
              </p>
            </div>
          </div>
        )}

        {step === 'notifications' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              These controls decide when you get pinged about a visitor. You can change them any
              time from your Account page.
            </p>
            <ToggleRow
              label="When any visitor checks in"
              description="A short notification when someone scans in at the gate."
              checked={notifyArrival}
              onChange={setNotifyArrival}
            />
            <ToggleRow
              label="When a walk-in is routed to an employee"
              description="You see it too — useful when an employee is unreachable."
              checked={notifyWalkin}
              onChange={setNotifyWalkin}
            />
            <ToggleRow
              label="Require my approval for employee-issued invites"
              description="On by default off — only turn on if you want to vet every visitor."
              checked={requireApproval}
              onChange={setRequireApproval}
            />
          </div>
        )}

        {step === 'employees' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Invite anyone who needs to issue visitor invitations. They’ll get an activation email
              and set their own password.
            </p>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {employees.map((emp, i) => (
                <div key={i} className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Full name"
                    value={emp.fullName}
                    onChange={(e) => updateEmployee(i, { fullName: e.target.value })}
                  />
                  <Input
                    type="email"
                    placeholder="email@company.com"
                    value={emp.email}
                    onChange={(e) => updateEmployee(i, { email: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addEmployeeRow}>
              <UserPlus className="size-4 mr-1.5" />
              Add another
            </Button>
            <p className="text-xs text-muted-foreground">
              You can skip this — invite employees later from the Employees page.
            </p>
          </div>
        )}

        {step === 'done' && (
          <div className="space-y-3 text-sm">
            <p>
              Your company is configured. You can revisit any of these settings from the{' '}
              <span className="font-medium text-foreground">Account</span> page.
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2">
                <Building2 className="size-4" />
                Company details and reception hours
              </li>
              <li className="flex items-center gap-2">
                <Mail className="size-4" />
                Visitor notification routing
              </li>
              <li className="flex items-center gap-2">
                <UserPlus className="size-4" />
                Add or remove employees
              </li>
            </ul>
          </div>
        )}

        <DialogFooter className="flex !justify-between gap-2">
          <Button variant="ghost" onClick={close} disabled={isPending}>
            {step === 'done' ? 'Close' : 'Skip for now'}
          </Button>
          <Button onClick={handleNext} disabled={isPending}>
            {step === 'done' ? 'Take me to my dashboard' : 'Continue'}
            <ChevronRight className="size-4 ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
      <div className="space-y-0.5">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
